'use client';

import { StaticImageData } from 'next/image';
import { twMerge } from 'tailwind-merge';

import { fonts } from '@/config';
import Breadcrumbs from '../BreadCrumbs';
import NextImage from '../NextImage';
import { usePathname } from 'next/navigation';
import { searchParams } from '@/utils/helper';

type ClassNamesTypes = {
  image?: string;
  root?: string;
  breadcrumbs?: { root?: string; list: { link?: string; dot?: string } };
  landingText?: string;
  title?: string;
  subTitle?: string;
};

interface HeroBannerPropTypes {
  bannerImg: StaticImageData | string;
  mobileBannerImg?: StaticImageData | string;
  title?: string;
  subTitle?: string;
  classNames?: ClassNamesTypes;
  breadcrumbs?: string | { path?: string; labelToUpperCase?: boolean };
  showBreadcrumbs?: boolean;
  objectFill?: 'cover' | 'contain' | 'fill';
  objectFillMobile?: 'cover' | 'contain' | 'fill';
  showOverlay?: boolean;
  redirectTo?: string;
  bannerAlt?: string;
  /** Image quality 1-100. Default 75. Use 90-95 for hero banners. */
  quality?: number;
  /** Responsive sizes for image loading. Use "100vw" for full-width heroes. */
  sizes?: string;
}

export default function HeroBanner({
  bannerImg,
  mobileBannerImg,
  title,
  subTitle,
  classNames,
  breadcrumbs,
  showBreadcrumbs = true,
  objectFill = 'cover',
  objectFillMobile = 'cover',
  showOverlay = true,
  redirectTo,
  bannerAlt,
  quality = 75,
  sizes = '70vw',
}: HeroBannerPropTypes) {
  const pathName = usePathname();
  const bc =
    typeof breadcrumbs === 'string'
      ? breadcrumbs
      : typeof breadcrumbs === 'object'
        ? breadcrumbs?.path
        : searchParams.get('breadcrumb');
  return (
    <div
      onClick={() => {
        if (redirectTo) {
          window.open(redirectTo, '_blank');
        }
      }}
      className={twMerge(
        `relative h-108 w-full md:h-[65vh]`,
        classNames?.root,
        redirectTo && 'cursor-pointer'
      )}>
      {showBreadcrumbs && (
        <Breadcrumbs
          omitIndexList={bc ? [] : pathName.includes('/form/') ? [0] : []}
          path={bc || undefined}
          labelsToUppercase={typeof breadcrumbs !== 'object'}
          classNames={{
            root: classNames?.breadcrumbs?.root ?? 'z-10 absolute top-0',
            list: classNames?.breadcrumbs?.list ?? {
              link: 'text-white',
              dot: 'bg-white',
            },
          }}
        />
      )}
      <div className='max-lg:hidden'>
        <NextImage
          className={twMerge(
            `rounded-none object-${objectFill}`,
            classNames?.image
          )}
          src={bannerImg}
          alt={bannerAlt ?? 'Banner'}
          fill
          placeholder='blur'
          priority
          quality={quality}
          blurDataURL='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPsSwMAAYgA9hK2NmIAAAAASUVORK5CYII='
          sizes={sizes}
        />
      </div>
      <div className='lg:hidden'>
        <NextImage
          className={twMerge(
            `rounded-none object-${objectFillMobile}`,
            !mobileBannerImg && 'object-cover',
            classNames?.image
          )}
          src={mobileBannerImg ?? bannerImg}
          alt='Banner'
          fill
          priority
          quality={quality}
          blurDataURL='data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mPsSwMAAYgA9hK2NmIAAAAASUVORK5CYII='
          sizes={sizes}
        />
      </div>
      {(title || subTitle) && (
        <div
          className={twMerge(
            'absolute bottom-0 left-0 right-0 top-0 h-full w-full select-none',
            showOverlay && 'bg-[rgba(0,0,0,0.5)]'
          )}>
          <div
            className={twMerge(
              'absolute left-1/2 top-1/2 w-full -translate-x-1/2 -translate-y-1/2 text-center text-white md:w-max md:max-w-200',
              classNames?.landingText
            )}>
            <div className='flex flex-col items-center justify-center'>
              <h1
                className={twMerge(
                  fonts?.russolo?.className,
                  'font-russolo mb-2 text-[2.5rem] font-semibold xl:text-6xl',
                  classNames?.title
                )}>
                {title}
              </h1>
              <p
                className={twMerge(
                  'font-gilroy w-[70%] text-base',
                  classNames?.subTitle
                )}>
                {subTitle}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
